import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Simplified single-screen expense form for Tier 1 (Field Operators).
 * 8-category list, no GSTIN/TDS/billable controls. Submits to existing /api/expenses/claim
 * with sensible defaults (city='Mumbai', purpose=description) so the existing backend
 * accepts it without backend changes.
 */
const CATEGORIES = [
  "Travel",
  "Per-diem",
  "Surveillance",
  "Test purchase",
  "Documents / Photocopy",
  "Communication",
  "Equipment",
  "Other",
];

export default function ExpenseQuickSubmit() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    amount: "",
    currency: "INR",
    category: CATEGORIES[0],
    description: "",
    photo: null,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const body = {
        travel_start: today,
        travel_end: today,
        city: "Mumbai",
        out_of_pocket_amount: Number(form.amount) || 0,
        currency: form.currency,
        purpose: `[${form.category}] ${form.description}`.trim(),
      };
      await api.post("/expenses/claim", body);
      setOk(true);
      setTimeout(() => nav("/my-tasks"), 1200);
    } catch (e) {
      setErr(e.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto space-y-5" data-testid="expense-quick-submit-page">
      <div>
        <div className="label-eyebrow">Field Expense</div>
        <h1 className="font-display text-[24px] font-bold tracking-tight mt-1">Submit an expense</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1">Takes 30 seconds.</div>
      </div>

      {ok ? (
        <div className="card-flat p-6 text-center" data-testid="expense-submitted-ok">
          <div className="font-display text-[18px] font-bold">Submitted ✓</div>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">Returning to My Tasks…</div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="card-flat p-5 space-y-4">
          <div>
            <label className="label-eyebrow block mb-1.5">Amount</label>
            <div className="flex gap-2">
              <input
                required
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="span-input font-mono flex-1 h-12 text-[18px]"
                data-testid="expense-amount"
                placeholder="0.00"
                inputMode="decimal"
              />
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="span-input font-mono w-[100px] h-12"
                data-testid="expense-currency"
              >
                {["INR", "USD", "SGD", "EUR", "BDT", "AED", "GBP"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-1.5">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="span-input h-12"
              data-testid="expense-category"
            >
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="label-eyebrow block mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="span-input min-h-[80px]"
              placeholder="e.g. Taxi from BKC to Andheri for case SPAN-INV-IN-0042"
              data-testid="expense-description"
            />
          </div>

          <div>
            <label className="label-eyebrow block mb-1.5">Photo / Receipt (optional)</label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setForm({ ...form, photo: e.target.files?.[0] || null })}
              className="span-input text-[12px]"
              data-testid="expense-photo"
            />
            {form.photo && <div className="text-[11px] text-[var(--span-text-2)] mt-1 font-mono">{form.photo.name}</div>}
          </div>

          {err && <div className="text-[12px] text-[var(--span-danger)]" data-testid="expense-error">{err}</div>}

          <button
            type="submit"
            disabled={busy}
            data-testid="expense-submit-btn"
            className="span-btn w-full h-12 text-[14px]"
          >
            {busy ? "Submitting…" : "Submit expense"}
          </button>
        </form>
      )}
    </div>
  );
}
